import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { queryClient } from '../lib/queryClient';
import { MANAGE } from '../config/navigation';
import ModeTransitionOverlay from '../components/layouts/ModeTransitionOverlay';
import {
  type ViewMode,
  LEARN_HOME,
  readStoredPreference,
  resolveMode,
  writeStoredPreference,
} from './viewMode.utils';

/** How long the branded cover stays up while the new surface mounts/fetches. */
const TRANSITION_MS = 800;

interface ViewModeContextType {
  /** Effective mode for the current page (route-aware, clamped by perms). */
  mode: ViewMode;
  /** True when the member has any management nav available (the toggle shows). */
  canManage: boolean;
  /** Management landing path for this member (first accessible manage item). */
  manageHome: string;
  /** True while the branded switch cover is visible. */
  isTransitioning: boolean;
  /** Switch view mode: persist → clear cache → cover → navigate → settle. */
  switchMode: (target: ViewMode) => void;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const ViewModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { can } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // The management nav this member can actually see drives both whether the
  // toggle appears and where "Manage" lands. Cheap to recompute each render.
  const manageVisible = MANAGE.filter((item) => !item.perm || can(item.perm));
  const canManage = manageVisible.length > 0;
  const manageHome = manageVisible[0]?.path ?? LEARN_HOME;

  const [preference, setPreference] = useState<ViewMode>(() => readStoredPreference() ?? 'manage');
  const [transitionTo, setTransitionTo] = useState<ViewMode | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mode = resolveMode({ pathname: location.pathname, preference, canManage });

  const switchMode = useCallback(
    (target: ViewMode) => {
      // Pure learners can never enter manage mode; ignore stray requests.
      if (target === 'manage' && !canManage) return;

      const dest = target === 'manage' ? manageHome : LEARN_HOME;
      setPreference(target);
      writeStoredPreference(target);
      // Query keys aren't mode-scoped, so drop the prior surface's cached data;
      // the destination's pages refetch fresh behind the cover. Signed asset
      // URLs are kept: they are immutable and not surface data, and dropping
      // them meant the workspace's own logo had to be re-fetched mid-switch —
      // so the cover showed the Tereiko mark instead of the customer's
      // (Aleix, 2026-09-11).
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'signed-url',
      });
      setTransitionTo(target);
      navigate(dest);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setTransitionTo(null);
        timerRef.current = null;
      }, TRANSITION_MS);
    },
    [canManage, manageHome, navigate]
  );

  // Clear a pending timer if the provider unmounts mid-transition.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <ViewModeContext.Provider
      value={{ mode, canManage, manageHome, isTransitioning: transitionTo !== null, switchMode }}
    >
      {children}
      <ModeTransitionOverlay show={transitionTo !== null} target={transitionTo ?? mode} />
    </ViewModeContext.Provider>
  );
};

export const useViewMode = (): ViewModeContextType => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return ctx;
};

export default ViewModeContext;
